import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Loader,
  MultiSelect,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type { Communication, Reference } from '@medplum/fhirtypes';
import { useMedplumProfile } from '@medplum/react-hooks';
import { IconMessage, IconPlus, IconSend, IconX } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePermissions } from '../../../contexts/PermissionContext';
import { useMessageableRecipients } from '../hooks/useMessageableRecipients';
import { useMessagingActions } from '../hooks/useMessagingActions';
import { getComposeThreadMeta } from '../services/messagingFhir';
import classes from './ComposeNewPanel.module.css';

const SPACE_CREATOR_ROLES = new Set(['unify-admin', 'practice-owner', 'practice-admin']);

export type ComposeRecipientType = 'client' | 'provider' | 'space';

const MOCK_RECIPIENTS = [
  { value: 'Practitioner/practitioner-101', label: 'Dr. Johnson', type: 'provider' as const },
  { value: 'Practitioner/practitioner-202', label: 'Sarah Wilson, LCSW', type: 'provider' as const },
  { value: 'Practitioner/practitioner-789', label: 'Dr. Smith', type: 'provider' as const },
  { value: 'Practitioner/practitioner-303', label: 'Maria Lopez, LPC', type: 'provider' as const },
  { value: 'Patient/patient-456', label: 'Jane Doe', type: 'client' as const },
  { value: 'Patient/patient-789', label: 'John Smith', type: 'client' as const },
  { value: 'Patient/patient-101', label: 'Emily Johnson', type: 'client' as const },
  { value: 'Patient/patient-102', label: 'Anthony Garcia', type: 'client' as const },
  { value: 'Patient/patient-103', label: 'Rachel Kim', type: 'client' as const },
];

// ── Space membership rule definitions (from PRD) ────────────────────────────

export type SpaceRuleType = 'role' | 'licensure' | 'supervision';

export interface SpaceRule {
  type: SpaceRuleType;
  value: string;
}

const RULE_TYPE_OPTIONS: Array<{ value: SpaceRuleType; label: string }> = [
  { value: 'role', label: 'Role' },
  { value: 'licensure', label: 'Licensure' },
  { value: 'supervision', label: 'Supervision' },
];

const RULE_VALUE_OPTIONS: Record<SpaceRuleType, Array<{ value: string; label: string }>> = {
  role: [
    { value: 'provider', label: 'Provider' },
    { value: 'supervisor', label: 'Supervisor' },
    { value: 'practice-owner', label: 'Practice Owner' },
    { value: 'practice-admin', label: 'Practice Admin' },
    { value: 'practice-assistant', label: 'Practice Assistant' },
    { value: 'unify-admin', label: 'Unify Admin' },
  ],
  licensure: [
    { value: 'independent', label: 'Independent (licensed)' },
    { value: 'pre-licensed', label: 'Pre-licensed' },
    { value: 'associate', label: 'Associate' },
  ],
  supervision: [
    { value: 'supervisor', label: 'Supervisor' },
  ],
};

/** Human-readable label for a rule */
function ruleLabel(rule: SpaceRule): string {
  const typeLabel = RULE_TYPE_OPTIONS.find((t) => t.value === rule.type)?.label ?? rule.type;
  const valLabel =
    RULE_VALUE_OPTIONS[rule.type]?.find((v) => v.value === rule.value)?.label ?? rule.value;
  return `${typeLabel}: ${valLabel}`;
}

// ── Component ────────────────────────────────────────────────────────────────

interface ComposeNewPanelProps {
  allowedRecipientTypes?: ComposeRecipientType[];
  onThreadCreated?: (thread: Communication) => void;
  onClose: () => void;
}

export function ComposeNewPanel({
  allowedRecipientTypes,
  onThreadCreated,
  onClose,
}: ComposeNewPanelProps): JSX.Element {
  const profile = useMedplumProfile();
  const { role } = usePermissions();
  const canCreateSpaces = SPACE_CREATOR_ROLES.has(role);

  const recipientTypeOptions = useMemo((): ComposeRecipientType[] => {
    const base: ComposeRecipientType[] =
      allowedRecipientTypes && allowedRecipientTypes.length > 0
        ? allowedRecipientTypes
        : ['client', 'provider', 'space'];
    return canCreateSpaces ? base : base.filter((t) => t !== 'space');
  }, [allowedRecipientTypes, canCreateSpaces]);

  const showTypePicker = recipientTypeOptions.length > 1;
  const [recipientType, setRecipientType] = useState<ComposeRecipientType>(
    recipientTypeOptions[0] ?? 'provider',
  );

  const [selectedRecipientRef, setSelectedRecipientRef] = useState<string | null>(null);
  const [selectedRecipientRefs, setSelectedRecipientRefs] = useState<string[]>([]);
  const [spaceName, setSpaceName] = useState('');
  const [spaceRules, setSpaceRules] = useState<SpaceRule[]>([]);

  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const { recipients, loading: recipientsLoading, restrictToOwnProviders } = useMessageableRecipients();
  const { createThread, isMock } = useMessagingActions();

  const isSpaceType = recipientType === 'space';
  const isClientType = recipientType === 'client';

  useEffect(() => {
    setSelectedRecipientRef(null);
    setSelectedRecipientRefs([]);
    setSpaceName('');
    setSpaceRules([]);
  }, [recipientType]);

  const recipientSelectData = useMemo(() => {
    if (isMock) {
      return MOCK_RECIPIENTS
        .filter((r) => {
          if (isClientType) return r.type === 'client';
          if (recipientType === 'provider' || isSpaceType) return r.type === 'provider';
          return true;
        })
        .map((r) => ({ ...r, preferredLabel: r.label }));
    }
    return recipients
      .filter((r) => {
        if (isClientType) return r.resourceType === 'Patient';
        if (recipientType === 'provider' || isSpaceType) return r.resourceType === 'Practitioner';
        return true;
      })
      .map((r) => ({
        value: r.reference,
        label: r.display,
        preferredLabel: r.resourceType === 'Practitioner' ? r.preferredDisplay : r.display,
      }));
  }, [isMock, recipients, isClientType, isSpaceType, recipientType]);

  const hasRecipient = isSpaceType
    ? (selectedRecipientRefs.length > 0 || spaceRules.length > 0) && spaceName.trim().length > 0
    : isClientType
      ? !!selectedRecipientRef
      : selectedRecipientRefs.length > 0;

  const addRule = useCallback((rule: SpaceRule) => {
    setSpaceRules((prev) => {
      if (prev.some((r) => r.type === rule.type && r.value === rule.value)) return prev;
      return [...prev, rule];
    });
  }, []);

  const removeRule = useCallback((idx: number) => {
    setSpaceRules((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleSend = async (): Promise<void> => {
    if (!message.trim()) {
      showNotification({ color: 'red', message: 'Please enter a message' });
      return;
    }

    if (!hasRecipient) {
      showNotification({ color: 'red', message: 'Please select a recipient' });
      return;
    }

    setSending(true);

    try {
      let recipientRefs: Reference[];
      let categoryCode: string;
      let categoryDisplay: string;
      let subjectRef: Reference | undefined;
      let topicText: string | undefined;

      const resolveWriteDisplay = (ref: string): string =>
        recipientSelectData.find((r) => r.value === ref)?.preferredLabel ?? '';
      const writeReference = (ref: string): Reference => {
        const display = resolveWriteDisplay(ref);
        return { reference: ref, ...(display ? { display } : {}) };
      };

      if (isSpaceType) {
        recipientRefs = selectedRecipientRefs.map(writeReference);
        categoryCode = 'space';
        categoryDisplay = 'Space';
        subjectRef = undefined;
        topicText = spaceName.trim();
      } else if (isClientType) {
        const recipientRef: Reference = selectedRecipientRef
          ? writeReference(selectedRecipientRef)
          : { reference: 'Patient/unknown', display: 'Unknown' };

        const recipientResType: 'Patient' | 'Practitioner' | undefined =
          selectedRecipientRef?.startsWith('Patient/') ? 'Patient' : 'Practitioner';

        const meta = getComposeThreadMeta(
          profile ?? undefined,
          recipientRef,
          recipientResType,
        );
        categoryCode = meta.categoryCode;
        categoryDisplay = meta.categoryDisplay;
        subjectRef = meta.subjectRef;
        recipientRefs = [recipientRef];
      } else {
        recipientRefs = selectedRecipientRefs.map(writeReference);

        if (recipientRefs.length === 1) {
          const meta = getComposeThreadMeta(
            profile ?? undefined,
            recipientRefs[0],
            'Practitioner',
          );
          categoryCode = meta.categoryCode;
          categoryDisplay = meta.categoryDisplay;
          subjectRef = meta.subjectRef;
        } else {
          categoryCode = 'provider-conversation';
          categoryDisplay = 'Provider Conversation';
          subjectRef = undefined;
        }
      }

      const { thread } = await createThread({
        recipients: recipientRefs,
        message: message.trim(),
        categoryCode,
        categoryDisplay,
        subjectRef,
        topicText,
        spaceRules: isSpaceType && spaceRules.length > 0 ? spaceRules : undefined,
      });

      showNotification({ color: 'green', message: 'Message sent' });
      setSending(false);
      onClose();

      if (onThreadCreated && thread.id) {
        onThreadCreated(thread);
      }
    } catch (err) {
      console.error('[Messaging] Failed to create thread:', err);
      showNotification({ color: 'red', message: 'Failed to send message' });
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  return (
    <Box className={classes.panel}>
      {/* Header */}
      <Group justify="space-between" className={classes.header}>
        <Text fw={600} size="md">
          {isSpaceType ? 'Create space' : 'New message'}
        </Text>
        <Tooltip label="Close">
          <ActionIcon variant="subtle" color="gray" onClick={onClose} aria-label="Close compose">
            <IconX size={18} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {/* Recipient fields */}
      <Box className={classes.recipientBar}>
        <Stack gap="sm">
          <Group gap="sm" align="flex-end" wrap="nowrap">
            {showTypePicker && (
              <Select
                label="Type"
                size="sm"
                w={130}
                value={recipientType}
                onChange={(val) => {
                  if (val) setRecipientType(val as ComposeRecipientType);
                }}
                data={recipientTypeOptions.map((t) => ({
                  value: t,
                  label: t === 'client' ? 'Client' : t === 'provider' ? 'Provider' : 'Space',
                }))}
                allowDeselect={false}
              />
            )}

            {isSpaceType ? (
              <TextInput
                label="Space name"
                placeholder="e.g. Licensed Therapists, All Staff..."
                size="sm"
                value={spaceName}
                onChange={(e) => setSpaceName(e.target.value)}
                style={{ flex: 1 }}
              />
            ) : null}
          </Group>

          {/* Space: membership rules + manual members */}
          {isSpaceType ? (
            <>
              <SpaceRulesBuilder rules={spaceRules} onAdd={addRule} onRemove={removeRule} />
              <Divider
                label={<Text size="xs" c="dimmed">Additional members (manual)</Text>}
                labelPosition="left"
              />
              <MultiSelect
                placeholder="Add individual members..."
                size="sm"
                value={selectedRecipientRefs}
                onChange={setSelectedRecipientRefs}
                data={recipientSelectData}
                searchable
                nothingFoundMessage="No eligible members"
                disabled={!isMock && recipientsLoading}
                rightSection={!isMock && recipientsLoading ? <Loader size="xs" /> : undefined}
              />
            </>
          ) : isClientType ? (
            <Select
              description={
                !isMock && restrictToOwnProviders
                  ? 'You can only message your assigned providers.'
                  : undefined
              }
              placeholder={recipientsLoading ? 'Loading...' : 'Search for a client...'}
              size="sm"
              value={selectedRecipientRef}
              onChange={setSelectedRecipientRef}
              data={recipientSelectData}
              searchable
              nothingFoundMessage="No eligible recipients"
              disabled={!isMock && recipientsLoading}
              rightSection={!isMock && recipientsLoading ? <Loader size="xs" /> : undefined}
            />
          ) : (
            <MultiSelect
              description={
                !isMock && restrictToOwnProviders
                  ? 'You can only message your assigned providers.'
                  : undefined
              }
              placeholder={
                !isMock && recipientsLoading ? 'Loading...' : 'Search for providers...'
              }
              size="sm"
              value={selectedRecipientRefs}
              onChange={setSelectedRecipientRefs}
              data={recipientSelectData}
              searchable
              nothingFoundMessage="No eligible recipients"
              disabled={!isMock && recipientsLoading}
              rightSection={!isMock && recipientsLoading ? <Loader size="xs" /> : undefined}
            />
          )}
        </Stack>
      </Box>

      {/* Message area (fills remaining space) */}
      <Box className={classes.messageArea}>
        <Box className={classes.emptyHint}>
          <IconMessage size={40} stroke={1} style={{ color: 'var(--mantine-color-gray-4)' }} />
          <Text size="sm" c="dimmed">
            {isSpaceType ? 'Send the first message to create this space' : 'Start a new conversation'}
          </Text>
        </Box>
      </Box>

      {/* Compose bar at bottom */}
      <Box className={classes.composeFooter}>
        <Text size="xs" c="dimmed" mb="xs">
          Messages are stored securely. Access follows your care team permissions.
        </Text>
        <Group gap="sm" align="flex-end">
          <Textarea
            placeholder="Write a message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            autosize
            minRows={1}
            maxRows={4}
            style={{ flex: 1 }}
            disabled={sending}
          />
          <Button
            onClick={() => void handleSend()}
            disabled={sending || !message.trim() || !hasRecipient}
            loading={sending}
            color="unifyGold"
            leftSection={!sending ? <IconSend size={18} /> : undefined}
            aria-label="Send message"
          >
            {isSpaceType ? 'Create' : 'Send'}
          </Button>
        </Group>
      </Box>
    </Box>
  );
}

// ── Space Rules Builder ──────────────────────────────────────────────────────

interface SpaceRulesBuilderProps {
  rules: SpaceRule[];
  onAdd: (rule: SpaceRule) => void;
  onRemove: (idx: number) => void;
}

function SpaceRulesBuilder({ rules, onAdd, onRemove }: SpaceRulesBuilderProps): JSX.Element {
  const [ruleType, setRuleType] = useState<SpaceRuleType | null>(null);
  const [ruleValue, setRuleValue] = useState<string | null>(null);

  const availableValues = ruleType ? RULE_VALUE_OPTIONS[ruleType] : [];

  const handleAddRule = (): void => {
    if (!ruleType || !ruleValue) return;
    onAdd({ type: ruleType, value: ruleValue });
    setRuleType(null);
    setRuleValue(null);
  };

  return (
    <Box>
      <Text size="sm" fw={500} mb={4}>
        Auto-membership rules
      </Text>
      <Text size="xs" c="dimmed" mb="sm">
        Members matching any rule are automatically added. Rules are additive (union).
      </Text>

      {rules.length > 0 && (
        <Group gap="xs" mb="sm" wrap="wrap">
          {rules.map((rule, idx) => (
            <Badge
              key={`${rule.type}-${rule.value}`}
              variant="light"
              color="unifySage"
              size="lg"
              rightSection={
                <ActionIcon
                  size="xs"
                  variant="transparent"
                  color="unifySage"
                  onClick={() => onRemove(idx)}
                  aria-label={`Remove rule: ${ruleLabel(rule)}`}
                >
                  <IconX size={12} />
                </ActionIcon>
              }
            >
              {ruleLabel(rule)}
            </Badge>
          ))}
        </Group>
      )}

      <Group gap="xs" align="flex-end" wrap="nowrap">
        <Select
          placeholder="Rule type"
          size="xs"
          w={140}
          value={ruleType}
          onChange={(val) => {
            setRuleType(val as SpaceRuleType | null);
            setRuleValue(null);
          }}
          data={RULE_TYPE_OPTIONS}
          clearable
        />
        <Select
          placeholder={ruleType ? 'Select value' : 'Choose type first'}
          size="xs"
          style={{ flex: 1 }}
          value={ruleValue}
          onChange={setRuleValue}
          data={availableValues}
          disabled={!ruleType}
          clearable
        />
        <Tooltip label="Add rule">
          <ActionIcon
            variant="light"
            color="unifyGold"
            size="md"
            disabled={!ruleType || !ruleValue}
            onClick={handleAddRule}
            aria-label="Add rule"
          >
            <IconPlus size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Box>
  );
}
